/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import heritageData from "~/data/home/heritage.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";
import { LANG } from "~/utils/constant";
import ClientOnly from "../shared-components/ClentOnly";

type HeritageData = {
  id: number;
  enTitle: string;
  guTitle: string;
  enDescription: string;
  guDescription: string;
  images: string;
  navigateTo: string;
};

type AboutUsItem = {
  item: HeritageData;
  className?: string;
  alignment?: "left" | "center" | "right";
  imageSize?: number;
  cardIndex?: number;
};

function AboutUs() {
  const lang = useLanguageParams();
  const { navigate } = useLocale();
  const { medium, normalInter } = useFontStyle();
  const { t, ready } = useTranslation("common");

  const AboutUsCard = ({ item, className, alignment, cardIndex }: AboutUsItem) => {
    // Create continuous design flow across cards
    const getBackgroundDesign = () => {
      switch (cardIndex) {
        case 0:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -right-28 w-[200px] h-[200px] opacity-[0.08] object-cover select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-10 -left-10 w-[120px] h-[120px] opacity-[0.08] object-cover rotate-12 select-none"
              />
            </>
          );

        case 1:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -left-[115px] w-[200px] h-[200px] opacity-[0.08] object-cover select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -bottom-[2.75rem] -right-[2.25rem] w-[120px] h-[120px] opacity-[0.08] object-cover -rotate-12 select-none"
              />
            </>
          );

        case 2:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute top-4 -right-[5rem] w-[160px] h-[160px] opacity-[0.08] object-cover rotate-45 select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -bottom-5 -left-8 w-[100px] h-[100px] opacity-[0.08] object-cover select-none"
              />
            </>
          );

        case 3:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute top-4 -left-[5rem] w-[160px] h-[160px] opacity-[0.08] object-cover rotate-45 select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-[3rem] -right-[3rem] w-[120px] h-[120px] opacity-[0.08] object-cover rotate-45 select-none"
              />
            </>
          );

        case 4:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-[3rem] -left-[3rem] w-[120px] h-[120px] opacity-[0.08] object-cover rotate-45 select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -bottom-[2.75rem] -right-[2.25rem] w-[160px] h-[160px] opacity-[0.08] object-cover select-none"
              />
            </>
          );

        default:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-10 -left-10 w-[119px] h-[133px] opacity-[0.08] object-cover select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute top-10 -right-32 w-[204px] h-[204px] opacity-[0.08] object-cover select-none"
              />
            </>
          );
      }
    };

    return (
      <div
        className={cn(
          "w-full h-full flex justify-center items-center gap-4 p-4 md:p-9 bg-homePageCardColor rounded-[12px] cursor-pointer relative overflow-hidden group",
          "flex-col min-h-[280px] md:min-h-0",
          className,
        )}
        onClick={() => navigate(item.navigateTo)}
      >
        {getBackgroundDesign()}

        <div
          className={cn(
            "rounded-full bg-slate-300 z-[1] shadow-md shadow-darkOrangeColor/20 flex-shrink-0",
            "w-[100px] h-[100px] 2xl:w-[130px] 2xl:h-[130px]",
            alignment === "center" ? "" : "sx:w-[100px] xs:h-[100px]",
          )}
        >
          <img
            src={item.images}
            alt={lang === LANG.english ? item.enTitle : item.guTitle}
            className="w-full h-full object-cover rounded-full select-none"
          />
        </div>

        <div
          className={cn(
            "w-full h-full flex-1 flex flex-col justify-between items-center gap-4 text-center",
            `justify-${alignment || "center"} items-${alignment || "center"}`,
          )}
        >
          <div
            className={cn(
              "flex flex-col gap-2 w-full overflow-hidden line-clamp-4 text-center items-center",
              `justify-${alignment || "left"} items-${alignment || "left"}`,
            )}
          >
            <p className={cn("body_text_l text-textColor", `text-${alignment || "left"}`, medium)}>
              {lang === LANG.english ? item.enTitle : item.guTitle}
            </p>
            <p className={cn("body_text_m text-textSecondaryColor", `text-${alignment || "left"}`, normalInter)}>
              {lang === LANG.english ? item.enDescription : item.guDescription}
            </p>
          </div>

          <button
            className={cn(
              "w-full flex gap-2 text-textHeadingColor hover:underline font-inter",
              `text-${alignment || "left"} justify-${alignment || "left"} items-${alignment || "left"}`,
            )}
          >
            <span className={cn("body_text_s flex justify-center items-center gap-2", normalInter)}>
              {t("common.read_more", { defaultValue: "Read more" })} <ArrowRight size={16} />
            </span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <ClientOnly
      fallback={
        <div className="w-full flex flex-col justify-start items-center">
          {/* Header Section - Non-animated fallback */}
          <div className="w-full flex flex-col gap-4 justify-center items-center ">
            <div className="flex flex-col justify-center items-center gap_btw_title_content">
              <span className={cn("!text-textHeadingColor body_text_m capitalize", medium)}>
                {t("home.swaminarayanBhagawan", { defaultValue: "Swaminarayan Bhagwan" })}
              </span>
              <p className={cn("home-page-heading text-center font-medium capitalize", medium)}>{t("home.aboutUs", { defaultValue: "About Us" })}</p>
              <span className={cn("text-textSecondaryColor body_text_l text-center ", normalInter)}>
                {t("home.aboutUsDescription", {
                  defaultValue:
                    "Jay Swaminarayan! We are a global community dedicated to the teachings and values of Bhagwan Swaminarayan. Our mission is to spread love, peace, and spirituality through various initiatives and activities.",
                })}
              </span>
            </div>

            <div className="w-full h-full grid grid-cols-1 md:grid-cols-3 gap-4 2xl:gap-6">
              {heritageData.slice(0, 3).map((item, index) => (
                <AboutUsCard key={index} item={item} className="flex-col h-full" alignment="center" imageSize={122} cardIndex={index + 2} />
              ))}
            </div>
          </div>
        </div>
      }
    >
      <div className="w-full flex flex-col justify-start items-center">
        {/* Header Section */}
        <motion.div
          className="w-full flex flex-col gap-4 justify-center items-center "
          // initial={{ opacity: 0, y: 30 }}
          // whileInView={{ opacity: 1, y: 0 }}
          // viewport={{ once: true }}
          // transition={{ duration: 1.5 }}
        >
          <div className="flex flex-col justify-center items-center gap_btw_title_content">
            <span className={cn("!text-textHeadingColor body_text_m capitalize", medium)}>
              {t("home.swaminarayanBhagawan", { defaultValue: "Swaminarayan Bhagwan" })}
            </span>
            <p className={cn("home-page-heading text-center font-medium capitalize", medium)}>{t("home.aboutUs", { defaultValue: "About Us" })}</p>
            <span className={cn("text-textSecondaryColor body_text_l text-center ", normalInter)}>
              {t("home.aboutUsDescription", {
                defaultValue:
                  "Jay Swaminarayan! We are a global community dedicated to the teachings and values of Bhagwan Swaminarayan. Our mission is to spread love, peace, and spirituality through various initiatives and activities.",
              })}
            </span>
          </div>

          <div className="w-full h-full grid grid-cols-1 md:grid-cols-3 gap-4 2xl:gap-6">
            {heritageData.slice(0, 3).map((item, index) => (
              <AboutUsCard key={index} item={item} className="flex-col h-full" alignment="center" imageSize={122} cardIndex={index + 2} />
            ))}
          </div>
        </motion.div>
      </div>
    </ClientOnly>
  );
}

export default AboutUs;

{
  /* <div className="w-full flex flex-col md:flex-row gap-4 2xl:gap-6 justify-center items-stretch">
        {aboutUsItems.map((item, index) => (
          <motion.div
            key={item.id}
            className="relative flex-1 flex flex-col gap-4 p-5 bg-secondaryColor justify-center items-center lg:justify-start lg:items-start cursor-pointer overflow-hidden rounded-xl group"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            whileHover={{ y: -5 }}
            onClick={() => navigate(item.navigateTo)}
          >
            
            <div
              className="absolute bottom-0 left-0 w-full h-full bg-darkOrangeColor transition-all duration-700 ease-out group-hover:opacity-100 opacity-0 "
              style={{ clipPath: "circle(0% at 0% 100%)", transition: "clip-path 1s ease-out, opacity 0.7s ease-out" }}
            />

            
            <style dangerouslySetInnerHTML={{ __html: `.group:hover .absolute:first-of-type { clip-path: circle(150% at 0% 100%) !important; }` }} />

            
            <div className="relative z-10 flex w-[40%] items-center justify-center rounded-full aspect-square overflow-hidden shadow-xl shadow-darkOrangeColor/20 ">
              <img src={item.image} alt={lang === LANG.english ? item.enTitle : item.guTitle} className="h-full w-full rounded-full object-cover" />
            </div>

            
            <div className="relative z-[1] flex flex-col items-center lg:items-start gap-2 flex-1">
              <p
                className={cn(
                  "text-textColor group-hover:text-white text-lg  transition-colors duration-300  sm:line-clamp-1 md:line-clamp-1 md:leading-5 lg:leading-normal lg:line-clamp-1 2xl:line-clamp-2 overflow-hidden",
                  medium
                )}
              >
                {lang === LANG.english ? item.enTitle : item.guTitle}
              </p>
              <p
                className={cn(
                  "text-textSecondaryColor group-hover:text-white/90 text-sm md:text-base xs:text-center md:text-start font-inter leading-relaxed transition-colors duration-300 overflow-hidden line-clamp-3",
                  normalInter
                )}
              >
                {lang === LANG.english ? item.enDescription : item.guDescription}
              </p>
            </div>

            
            <div className="w-full border-b border-borderPrimary z-[1]" />

            
            <motion.button
              className="relative z-[1] flex items-center gap-2 text-textHeadingColor group-hover:text-white font-inter font-medium text-sm transition-colors duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>{`${t("common.read_more")}`}</span>
              <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                <ArrowRight size={16} />
              </motion.div>
            </motion.button>
          </motion.div>
        ))}
      </div> */
}
